import { useCallback, useRef } from "react";

export function useComposerHistory(setMessage, messageRef, textRef) {
  const composerHistoryRef = useRef([]);
  const composerFutureRef = useRef([]);
  const isApplyingComposerStateRef = useRef(false);

  const applyComposerState = useCallback((nextMessage, selectionStart = null, selectionEnd = null) => {
    isApplyingComposerStateRef.current = true;
    setMessage(nextMessage);

    requestAnimationFrame(() => {
      const textarea = textRef.current;
      if (textarea && selectionStart !== null && selectionEnd !== null) {
        textarea.focus();
        textarea.setSelectionRange(selectionStart, selectionEnd);
      }
      isApplyingComposerStateRef.current = false;
    });
  }, [setMessage, textRef]);

  const saveComposerHistory = useCallback((currentMessage) => {
    composerHistoryRef.current.push(currentMessage);
    if (composerHistoryRef.current.length > 100) {
      composerHistoryRef.current.shift();
    }
    composerFutureRef.current = [];
  }, []);

  const handleComposerChange = useCallback((e) => {
    const nextMessage = e.target.value;
    const currentMessage = messageRef.current || "";
    if (!isApplyingComposerStateRef.current && nextMessage !== currentMessage) {
      saveComposerHistory(currentMessage);
    }
    setMessage(nextMessage);
  }, [setMessage, messageRef, saveComposerHistory]);

  const handleComposerUndo = useCallback(() => {
    if (composerHistoryRef.current.length === 0) return;
    const currentMessage = messageRef.current || "";
    const previousMessage = composerHistoryRef.current.pop();
    composerFutureRef.current.push(currentMessage);
    applyComposerState(previousMessage, previousMessage.length, previousMessage.length);
  }, [messageRef, applyComposerState]);

  const handleComposerRedo = useCallback(() => {
    if (composerFutureRef.current.length === 0) return;
    const currentMessage = messageRef.current || "";
    const nextMessage = composerFutureRef.current.pop();
    composerHistoryRef.current.push(currentMessage);
    applyComposerState(nextMessage, nextMessage.length, nextMessage.length);
  }, [messageRef, applyComposerState]);

  return {
    composerHistoryRef,
    composerFutureRef,
    isApplyingComposerStateRef,
    applyComposerState,
    saveComposerHistory,
    handleComposerChange,
    handleComposerUndo,
    handleComposerRedo,
  };
}
